import React, { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalCloseButton, Button, StatusBadge } from '../common';
import { fetchPlayerClubStats } from '../../services/playerService';
import { updateGuest, updateGuestEloRatingsForClubSeason } from '../../services/guestService';
import { setManualLatePenalty } from '../../services/sessionService';
import { isLikelyNetworkError } from '../../utils/session/offlineQueueConstants';

const labelClass = 'block text-sm font-medium text-text-secondary mb-1';
const inputClass =
  'w-full px-3 py-2 border border-border-medium rounded-lg bg-surface text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-surface-subtle disabled:cursor-not-allowed';

/** Resolves what the edit form shows: guest name/gender vs late join penalty only. */
const resolveEditFocus = (focus, { isGuest, sessionId, allowLateJoinPenalty = true }) => {
  const penaltyOk = allowLateJoinPenalty && Boolean(sessionId);
  if (focus === 'guest_profile') return isGuest ? 'guest_profile' : penaltyOk ? 'session_penalty' : null;
  if (focus === 'session_penalty') return penaltyOk ? 'session_penalty' : null;
  if (focus === 'auto') {
    if (isGuest) return 'guest_profile';
    return penaltyOk ? 'session_penalty' : null;
  }
  return null;
};

const PlayerInfoModal = ({
  isOpen,
  onClose,
  player,
  clubId,
  seasonId,
  sessionId = null,
  onGuestUpdated,
  onPenaltyUpdated,
  sessionPlayerStatus = {},
  sessionPlayerPenalties = {},
  initialViewMode = 'info',
  initialEditFocus = 'auto',
  /** When false (Peg Board), hide late join penalty actions and fields. */
  allowLateJoinPenalty = true,
  /** From host roster: mean effective games among session players with status "playing" ({ average, count }). */
  sessionPlayingAvgEffectiveGames = null,
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [editFocus, setEditFocus] = useState(initialEditFocus);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    gender: 'male',
    lateJoinerPenalty: 0,
    mixedElo: '1200',
    levelsElo: '1200',
    singlesElo: '1000',
  });
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch stats when modal opens
  useEffect(() => {
    if (isOpen && player && clubId && seasonId) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, player?.id, clubId, seasonId]);

  // Reset form when player changes or modal opens
  // Use player.id and the specific penalty value to avoid infinite loops
  const currentPenaltyValue = player?.id ? (sessionPlayerPenalties?.[player.id] || 0) : 0;
  useEffect(() => {
    if (isOpen && player) {
      setEditForm({
        firstName: player.first_name || '',
        lastName: player.last_name || '',
        gender: player.gender || 'male',
        lateJoinerPenalty: currentPenaltyValue,
        mixedElo: String(player.mixed_elo_rating ?? 1200),
        levelsElo: String(player.levels_elo_rating ?? 1200),
        singlesElo: String(
          player.singles_elo_rating != null ? player.singles_elo_rating : 1000
        ),
      });
      setViewMode(initialViewMode);
      setEditFocus(initialEditFocus);
      setError(null);
      setEditError(null);
    }
  }, [isOpen, player?.id, currentPenaltyValue, initialViewMode, initialEditFocus]);

  const fetchStats = async () => {
    if (!player || !clubId || !seasonId) return;

    setLoading(true);
    setError(null);
    try {
      const playerStats = await fetchPlayerClubStats(player.id, clubId, seasonId);
      setStats(playerStats);
    } catch (err) {
      console.error('Error fetching player stats:', err);
      setError(err.message || 'Failed to load player statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!player) return;

    setSaving(true);
    setEditError(null);

    try {
      const effective = resolveEditFocus(editFocus, {
        isGuest: player.is_guest,
        sessionId,
        allowLateJoinPenalty,
      });

      if (!effective) {
        setEditError('Nothing to save in this view.');
        setSaving(false);
        return;
      }

      if (effective === 'guest_profile') {
        const trimmedFirst = editForm.firstName.trim();
        const trimmedLast = editForm.lastName.trim();

        if (!trimmedFirst) {
          setEditError('First name is required');
          setSaving(false);
          return;
        }

        await updateGuest(player.id, {
          first_name: trimmedFirst,
          last_name: trimmedLast || null,
          gender: editForm.gender,
        });

        const allowStartingElo =
          player.is_guest &&
          clubId &&
          seasonId &&
          stats &&
          stats.totalGamesPlayed === 0;

        if (allowStartingElo) {
          await updateGuestEloRatingsForClubSeason(player.id, clubId, seasonId, {
            mixed_elo_rating: editForm.mixedElo,
            levels_elo_rating: editForm.levelsElo,
            singles_elo_rating: editForm.singlesElo,
          });
        }
      }

      if (effective === 'session_penalty' && sessionId) {
        const penalty = parseInt(editForm.lateJoinerPenalty, 10) || 0;
        try {
          await setManualLatePenalty(sessionId, player.id, penalty);
        } catch (penaltyError) {
          if (!isLikelyNetworkError(penaltyError) || typeof onPenaltyUpdated !== 'function') {
            throw penaltyError;
          }
        }

        if (typeof onPenaltyUpdated === 'function') {
          await onPenaltyUpdated(player.id, penalty);
        }
      }

      // Refresh stats and return to info view
      await fetchStats();
      setViewMode('info');

      if (effective === 'guest_profile' && typeof onGuestUpdated === 'function') {
        await onGuestUpdated();
      }
    } catch (err) {
      console.error('[PlayerInfoModal] Failed to update:', err);
      setEditError(err.message || 'Failed to update. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (saving) return;
    setViewMode('info');
    setEditFocus(initialEditFocus);
    setEditError(null);
    // Reset form to original values
    if (player) {
      const currentPenalty = sessionPlayerPenalties[player.id] || 0;
      setEditForm({
        firstName: player.first_name || '',
        lastName: player.last_name || '',
        gender: player.gender || 'male',
        lateJoinerPenalty: currentPenalty,
        mixedElo: String(player.mixed_elo_rating ?? 1200),
        levelsElo: String(player.levels_elo_rating ?? 1200),
        singlesElo: String(
          player.singles_elo_rating != null ? player.singles_elo_rating : 1000
        ),
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No Sessions';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'No Sessions';
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const getStatusBadge = (status) => {
    if (!status) {
      return <StatusBadge variant="ghost" size="small">Not Signed Up</StatusBadge>;
    }
    
    switch (status) {
      case 'active':
        return <StatusBadge variant="success" size="small">Playing</StatusBadge>;
      case 'signed_up':
        return <StatusBadge variant="primary" size="small">Signed Up</StatusBadge>;
      case 'paused':
        return <StatusBadge variant="warning" size="small">Paused</StatusBadge>;
      case 'inactive':
        return <StatusBadge variant="secondary" size="small">Not Playing</StatusBadge>;
      case 'no_show':
        return <StatusBadge variant="warning" size="small">No Show</StatusBadge>;
      case 'cancelled':
        return <StatusBadge variant="danger" size="small">Cancelled</StatusBadge>;
      default:
        return <StatusBadge variant="ghost" size="small">Not Signed Up</StatusBadge>;
    }
  };

  const getGenderDisplay = (gender) => {
    return gender === 'female' ? 'Female' : 'Male';
  };

  if (!isOpen || !player) return null;

  const canEditStartingElo =
    Boolean(player.is_guest && clubId && seasonId && stats && stats.totalGamesPlayed === 0);

  const currentStatus = sessionPlayerStatus[player.id];
  const effectiveEditFocus = resolveEditFocus(editFocus, {
    isGuest: player.is_guest,
    sessionId,
    allowLateJoinPenalty,
  });
  const showGuestFields = effectiveEditFocus === 'guest_profile' && player.is_guest;
  const showPenaltyField =
    effectiveEditFocus === 'session_penalty' && Boolean(sessionId) && allowLateJoinPenalty;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium">
        <ModalCloseButton onClose={onClose} />
        
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-surface-subtle rounded-full flex items-center justify-center flex-shrink-0 border border-border-medium">
              <span className="text-2xl font-semibold text-text-secondary">
                {player.name?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-text-primary">
                {player.name || 'Unknown Player'}
              </h3>
              {player.is_guest && (
                <span className="text-sm font-medium text-primary dark:text-info bg-primary/15 dark:bg-primary/20 border border-primary/40 rounded px-2 py-0.5 inline-block mt-1">
                  Guest
                </span>
              )}
            </div>
          </div>

          {/* Info View */}
          {viewMode === 'info' && (
            <>
              {loading ? (
                <div className="text-center py-8 text-text-muted">
                  Loading player statistics...
                </div>
              ) : error ? (
                <div className="p-4 rounded border border-danger/30 bg-danger-light text-sm text-danger-dark">
                  {error}
                </div>
              ) : stats ? (
                <div className="space-y-4">
                  {/* Statistics Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                      <div className="text-sm text-text-secondary mb-1">Mixed ELO</div>
                      <div className="text-2xl font-semibold text-text-primary">
                        {formatNumber(stats.mixedElo)}
                      </div>
                    </div>
                    <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                      <div className="text-sm text-text-secondary mb-1">Levels ELO</div>
                      <div className="text-2xl font-semibold text-text-primary">
                        {formatNumber(stats.levelsElo)}
                      </div>
                    </div>
                    <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                      <div className="text-sm text-text-secondary mb-1">Singles ELO</div>
                      <div className="text-2xl font-semibold text-text-primary">
                        {formatNumber(stats.singlesElo)}
                      </div>
                    </div>
                    <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                      <div className="text-sm text-text-secondary mb-1">Sessions Played</div>
                      <div className="text-2xl font-semibold text-text-primary">
                        {stats.sessionsPlayed}
                      </div>
                    </div>
                    <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                      <div className="text-sm text-text-secondary mb-1">Total Games</div>
                      <div className="text-2xl font-semibold text-text-primary">
                        {stats.totalGamesPlayed}
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="space-y-3 pt-2 border-t border-border-medium">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-text-secondary">Last Session</span>
                      <span className="text-sm font-medium text-text-primary">
                        {formatDate(stats.lastSessionDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-text-secondary">Gender</span>
                      <span className="text-sm font-medium text-text-primary">
                        {getGenderDisplay(stats.gender)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-text-secondary">Member Type</span>
                      <span className="text-sm font-medium text-text-primary">
                        {stats.isGuest ? 'Guest' : 'Member'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-text-secondary">Current Session Status</span>
                      {getStatusBadge(currentStatus)}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Edit actions */}
              {(player.is_guest || (allowLateJoinPenalty && sessionId)) && (
              <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2 pt-4 border-t border-border-medium">
                {player.is_guest && sessionId && (
                  <>
                    <Button
                      variant="primary"
                      size="small"
                      className="sm:order-1"
                      onClick={() => {
                        setEditFocus('guest_profile');
                        setViewMode('edit');
                      }}
                    >
                      Edit details
                    </Button>
                    {allowLateJoinPenalty ? (
                    <Button
                      variant="secondary"
                      size="small"
                      className="sm:order-2"
                      onClick={() => {
                        setEditFocus('session_penalty');
                        setViewMode('edit');
                      }}
                    >
                      Late Join Penalty
                    </Button>
                    ) : null}
                  </>
                )}
                {player.is_guest && !sessionId && (
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      setEditFocus('guest_profile');
                      setViewMode('edit');
                    }}
                  >
                    Edit details
                  </Button>
                )}
                {!player.is_guest && allowLateJoinPenalty && sessionId && (
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      setEditFocus('session_penalty');
                      setViewMode('edit');
                    }}
                  >
                    Late Join Penalty
                  </Button>
                )}
              </div>
              )}
            </>
          )}

          {/* Edit View */}
          {viewMode === 'edit' && (
            <>
              {/* Show stats at top (read-only) */}
              {stats && (
                <div className="p-4 bg-surface-subtle rounded-lg space-y-2 text-sm border border-border-medium text-text-primary">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-text-secondary">Mixed ELO: </span>
                      <span className="font-medium text-text-primary">{formatNumber(stats.mixedElo)}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Levels ELO: </span>
                      <span className="font-medium text-text-primary">{formatNumber(stats.levelsElo)}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Singles ELO: </span>
                      <span className="font-medium text-text-primary">{formatNumber(stats.singlesElo)}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Sessions: </span>
                      <span className="font-medium text-text-primary">{stats.sessionsPlayed}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Total Games: </span>
                      <span className="font-medium text-text-primary">{stats.totalGamesPlayed}</span>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSave} className="space-y-4">
                {editError && (
                  <div className="p-3 rounded border border-danger bg-danger-light text-sm">
                    <p className="text-danger-dark">{editError}</p>
                  </div>
                )}

                {showGuestFields && (
                  <>
                    <div>
                      <label className={labelClass} htmlFor={`player-info-first-${player.id}`}>
                        First Name *
                      </label>
                      <input
                        id={`player-info-first-${player.id}`}
                        type="text"
                        name="firstName"
                        value={editForm.firstName}
                        onChange={handleEditInputChange}
                        className={inputClass}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <label className={labelClass} htmlFor={`player-info-last-${player.id}`}>
                        Last Name (optional)
                      </label>
                      <input
                        id={`player-info-last-${player.id}`}
                        type="text"
                        name="lastName"
                        value={editForm.lastName}
                        onChange={handleEditInputChange}
                        className={inputClass}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <label className={labelClass} htmlFor={`player-info-gender-${player.id}`}>
                        Gender <span className="text-danger">*</span>
                      </label>
                      <select
                        id={`player-info-gender-${player.id}`}
                        name="gender"
                        value={editForm.gender}
                        onChange={handleEditInputChange}
                        className={inputClass}
                        disabled={saving}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>

                    {canEditStartingElo && (
                      <div className="space-y-3 border-t border-border-medium pt-4 mt-2">
                        <p className="text-sm font-medium text-text-primary">Starting ELO (this club)</p>
                        <p className="text-xs text-text-muted">
                          Only available before this guest has any recorded games at this club. Changes here do not alter matches already saved — they update starting ratings for matchmaking going forward.
                        </p>
                        <div>
                          <label className={labelClass} htmlFor={`player-info-mixed-elo-${player.id}`}>
                            Mixed ELO
                          </label>
                          <input
                            id={`player-info-mixed-elo-${player.id}`}
                            type="number"
                            name="mixedElo"
                            min={100}
                            max={4000}
                            value={editForm.mixedElo}
                            onChange={handleEditInputChange}
                            className={inputClass}
                            disabled={saving}
                          />
                        </div>
                        <div>
                          <label className={labelClass} htmlFor={`player-info-levels-elo-${player.id}`}>
                            Levels ELO
                          </label>
                          <input
                            id={`player-info-levels-elo-${player.id}`}
                            type="number"
                            name="levelsElo"
                            min={100}
                            max={4000}
                            value={editForm.levelsElo}
                            onChange={handleEditInputChange}
                            className={inputClass}
                            disabled={saving}
                          />
                        </div>
                        <div>
                          <label className={labelClass} htmlFor={`player-info-singles-elo-${player.id}`}>
                            Singles ELO
                          </label>
                          <input
                            id={`player-info-singles-elo-${player.id}`}
                            type="number"
                            name="singlesElo"
                            min={100}
                            max={4000}
                            value={editForm.singlesElo}
                            onChange={handleEditInputChange}
                            className={inputClass}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    )}

                    {player.is_guest && stats && !canEditStartingElo && (
                      <p className="text-xs text-text-muted border-t border-border-medium pt-3 mt-2">
                        Starting ELO is locked after this guest has recorded games at this club. Ratings shown above reflect club history.
                      </p>
                    )}
                  </>
                )}

                {showPenaltyField && (
                  <div>
                    {sessionPlayingAvgEffectiveGames &&
                      Number.isFinite(sessionPlayingAvgEffectiveGames.average) && (
                        <div className="mb-4 p-3 rounded-lg border border-border-medium bg-surface-subtle text-sm text-text-secondary">
                          <span className="text-text-primary font-medium">Session context: </span>
                          among {sessionPlayingAvgEffectiveGames.count} playing{' '}
                          {sessionPlayingAvgEffectiveGames.count === 1 ? 'player' : 'players'}, average
                          effective games this session is{' '}
                          <span className="font-semibold text-text-primary tabular-nums">
                            {Math.abs(sessionPlayingAvgEffectiveGames.average % 1) < 1e-9
                              ? Math.round(sessionPlayingAvgEffectiveGames.average)
                              : sessionPlayingAvgEffectiveGames.average.toFixed(1)}
                          </span>
                          . Use the penalty to add virtual games so matchmaking lines up with that average.
                        </div>
                      )}
                    <label className={labelClass} htmlFor={`player-info-penalty-${player.id}`}>
                      Late Join Penalty
                    </label>
                    <input
                      id={`player-info-penalty-${player.id}`}
                      type="number"
                      name="lateJoinerPenalty"
                      min="0"
                      max="20"
                      value={editForm.lateJoinerPenalty}
                      onChange={handleEditInputChange}
                      className={inputClass}
                      disabled={saving}
                    />
                    <p className="text-xs text-text-muted mt-1">
                      Adds to this player&apos;s games played for queue ordering so late joiners don&apos;t jump ahead of people who started earlier.
                    </p>
                  </div>
                )}

                {!showGuestFields && !showPenaltyField && (
                  <p className="text-sm text-text-muted">Nothing to edit in this context.</p>
                )}

                <div className="flex justify-end space-x-3 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="small"
                    disabled={saving || (!showGuestFields && !showPenaltyField)}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default PlayerInfoModal;

